// @section 1 App shell — v5 grid (sidebar + top bar + canvas + drawer).
// Routes + sidebar nav are driven by routes/manifest.tsx. To add a route:
// create the leaf under routes/ and add one entry to ROUTES — this file
// and v5/Sidebar.tsx stay untouched.

import { useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { CommandPalette } from './components/CommandPalette';
import { CaptureModal } from './components/CaptureModal';
import { ShutdownModal } from './components/ShutdownModal';
import { ToastHost, toast } from './components/Toast';
import { listTasks, createTask } from './api';
import { signOut } from './lib/authFetch';
import { ROUTES, SHORTCUT_MAP } from './routes/manifest';
import { AppShell } from './v5/AppShell';

export default function App() {
  const [cmdOpen, setCmdOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [shutdownOpen, setShutdownOpen] = useState(false);
  const [undone, setUndone] = useState<{ id: string; title: string }[]>([]);

  // @block 1.2 Body class — v5 dark canvas applies inside the authenticated app
  // only. AuthBoot's pre-login screen keeps its own gradient so the body
  // background doesn't flash teal during the MSAL handshake.
  useEffect(() => {
    document.body.classList.add('v5');
    return () => { document.body.classList.remove('v5'); };
  }, []);

  // @block 1.3 Global shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (!e.shiftKey && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdOpen(true); }
      if (e.shiftKey && (e.key === ' ' || e.code === 'Space')) { e.preventDefault(); setCaptureOpen(true); }
      if (e.shiftKey && e.key.toLowerCase() === 's') { e.preventDefault(); openShutdown(); }
      const target = SHORTCUT_MAP[e.key];
      if (target) {
        e.preventDefault();
        location.hash = '';
        history.pushState({}, '', target);
        dispatchEvent(new PopStateEvent('popstate'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const openShutdown = useCallback(async () => {
    try {
      const open = await listTasks({ status: 'todo' });
      setUndone(open.map((t) => ({ id: t.id, title: t.title })));
    } catch { setUndone([]); }
    setShutdownOpen(true);
  }, []);

  const onCaptureCommit = useCallback(async (text: string, c: { kind: string; dest: string }) => {
    setCaptureOpen(false);
    if (c.kind === 'task') {
      try {
        await createTask({ title: text, parentType: 'standalone' });
        toast(`✓ Routed to ${c.dest}`);
      } catch (e) {
        toast(`Failed to create task: ${e}`);
      }
    } else {
      toast(`${c.kind} captured → ${c.dest}`);
    }
  }, []);

  return (
    <>
      <AppShell
        onCapture={() => setCaptureOpen(true)}
        onQuickAction={() => setCmdOpen(true)}
        onSignOut={() => { void signOut(); }}
      >
        <Routes>
          {ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
        </Routes>
      </AppShell>

      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        onCapture={() => { setCmdOpen(false); setCaptureOpen(true); }}
        onShutdown={() => { setCmdOpen(false); openShutdown(); }}
      />
      <CaptureModal
        open={captureOpen}
        onClose={() => setCaptureOpen(false)}
        onCommit={onCaptureCommit}
      />
      <ShutdownModal
        open={shutdownOpen}
        onClose={() => setShutdownOpen(false)}
        undone={undone}
        onFinish={() => { setShutdownOpen(false); toast('🌙 Day closed.'); }}
      />
      <ToastHost />
    </>
  );
}
